import axios from 'axios'
import { API_ORIGIN } from '@/config/api'
// === Admin endpoints (authenticated) ===
import { api } from '@/services/apiClient'

// Public API client (no authentication)
export const publicApi = axios.create({
  baseURL: `${API_ORIGIN}/api/public`,
  withCredentials: false,
})

export interface MunicipalityPublic {
  id: string
  name: string
  ine: string
}

export interface Coordinates {
  lat: number
  lng: number
}

export interface PublicReportPayload {
  municipality_ine: string
  address?: string
  cadastral_ref?: string
  coordinates?: Coordinates
  message?: string
  reporter_name?: string
  reporter_email?: string
  reporter_phone?: string
  images: string[] // Base64 encoded images
  captcha_token: string
}

export interface PublicReportResponse {
  success: boolean
  message: string
}

export interface AoiPublic {
  ine_code: string | null
  name: string
  geometry: Record<string, unknown> | null // GeoJSON geometry
}

export const getMunicipalities = async (): Promise<MunicipalityPublic[]> => {
  const { data } = await publicApi.get<MunicipalityPublic[]>('/municipalities')
  return data
}

export const getPublicAoi = async (ineCode: string): Promise<AoiPublic | null> => {
  try {
    const { data } = await publicApi.get<AoiPublic>(`/aoi/${ineCode}`)
    return data
  } catch {
    return null
  }
}

export const submitReport = async (payload: PublicReportPayload): Promise<PublicReportResponse> => {
  const { data } = await publicApi.post<PublicReportResponse>('/report', payload)
  return data
}

export interface NotificationRecipient {
  id: string
  email: string
  name?: string
  is_active: boolean
}

export interface NotificationRecipientCreate {
  email: string
  name?: string
  is_active?: boolean
}

export const getNotificationRecipients = async (
  municipalityId: string
): Promise<NotificationRecipient[]> => {
  const { data } = await api.get<NotificationRecipient[]>(
    `/public/municipality/${municipalityId}/recipients`
  )
  return data
}

export const addNotificationRecipient = async (
  municipalityId: string,
  payload: NotificationRecipientCreate
): Promise<NotificationRecipient> => {
  const { data } = await api.post<NotificationRecipient>(
    `/public/municipality/${municipalityId}/recipients`,
    payload
  )
  return data
}

export const deleteNotificationRecipient = async (
  municipalityId: string,
  recipientId: string
): Promise<void> => {
  await api.delete(`/public/municipality/${municipalityId}/recipients/${recipientId}`)
}

export const toggleNotificationRecipient = async (
  municipalityId: string,
  recipientId: string
): Promise<NotificationRecipient> => {
  const { data } = await api.patch<NotificationRecipient>(
    `/public/municipality/${municipalityId}/recipients/${recipientId}/toggle`
  )
  return data
}
